<template>
  <div v-if="loaded">
    <SearchArea />
    <div class="px-5 md:px-0 md:w-95 mx-auto max-w-max">
      <BreadCrumb :product="product" />
      <div class="lg:flex justify-between py-8">
        <div class="w-pro-info lg:pr-10">
          <div class="md:flex">
            <div class="md:w-2/5 xl:w-1/2">
              <div class="lg:flex flex-row-reverse">
                <div class="flex flex-grow items-center justify-center">
                  <img :src="product.images[value]">
                </div>
                <div class="lg:pr-10 flex justify-between lg:block">
                  <div
                    v-for="(image, index) in product.images.slice(0, 4)"
                    :key="`product-image-${index}`"
                    @click="value = index"
                    class="border w-16 xl:w-20 my-2"
                  >
                    <img :src="image">
                  </div>
                </div>
              </div>
            </div>
            <div class="md:w-3/5 xl:w-1/2 md:pl-10 pt-10 md:pt-0">
              <h3 class="text-2xl lg:text-4xl leading-10 pb-2">
                {{ product.name }}
              </h3>
              <span class="capitalize">Model No: {{ product.sku }}</span>
              <div class="py-6 xl:py-12">
                <p class="capitalize">
                  Brand: <span class="text-blue">{{ product.brand }}</span>
                </p>
                <p class="capitalize py-1">
                  Sold By:
                  <router-link to="" class="text-blue">
                    {{ product.supplier.name }}
                  </router-link>
                </p>
                <p class="capitalize" v-if="product.certificates.length">
                  Certifications:
                  <span
                    v-for="(certificate, index) in product.certificates"
                    :key="`certification-${index}`"
                    class="text-blue"
                  >
                    <a href="" class="pr-1">
                      {{ certificate.type }},
                    </a>
                  </span>
                </p>
              </div>
              <div class="pb-5"> 
                <p class="text-2xl">{{ product.price }}</p>
                <div v-if="product.stockInUsa" class="flex items-center">
                  <img src="@/assets/icons/usa.svg" class="w-8">
                  <div class="pl-2 pt-1 text-xs">Stock in USA</div>
                </div>
              </div>
              <div class="flex flex-wrap relative pb-2 md:pb-5">
                <div class="w-1/2 md:w-auto flex-grow">
                  <p class="text-dark opacity-50 text-sm lg:px-3">Quantity</p>
                  <QuantityChanger
                    :product="product"
                    :quantity="quantity"
                    @setQuantity="changeQuantity"
                    @changeQuantity="changeQuantity"
                  />
                </div>
                <div class="md:px-2 w-1/2 md:w-auto flex-grow">
                  <p class="text-dark opacity-50 text-sm lg:px-3">Inco Terms</p>
                  <div class="pl-3 pr-2 border rounded-full">
                    <select
                      v-model="incoterm"
                      class="w-full h-10 bg-white rounded-full"
                      @change="updatePrice"
                    >
                      <option
                        v-for="(option, index) in product.incoTerms"
                        :key="`option-${index}`"
                        :value="option"
                        :selected="index === 0"
                      >
                        {{ option }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="flex-grow self-end pt-12 md:pt-0">
                  <button
                    @click="addToCart"
                    :disabled="illegalQuantity"
                    class="new-app-btn bg-blue w-full h-10"
                    :class="{'opacity-25': illegalQuantity}"
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
              <div>Price: {{ productTotal }}</div>
            </div>
          </div>
          <ProductDetailTabs :product="product" />
        </div>
        <PromotionSection />
      </div>
      <RelatedProductsSection
        v-if="product.relatedProducts.length"
        :products="product.relatedProducts"
      />
    </div>
    <CovidBanner />
    <div class="fixed z-100 opacity-25 flex items-center justify-center inset-0 p-16 text-8xl pointer-events-none">
      Preview page
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
import SearchArea from '@/shared/components/layout/SearchArea'
import BreadCrumb from '@/shared/components/product/BreadCrumb'
import QuantityChanger from '@/shared/components/product/productInfo/QuantityChanger'
import ProductDetailTabs from '@/shared/components/product/productInfo/ProductDetailTabs'
import PromotionSection from '@/shared/components/product/productInfo/PromotionSection'
import RelatedProductsSection from '@/shared/components/product/productInfo/RelatedProductsSection'
import CovidBanner from '@/shared/components/shared/CovidBanner'

export default {
  name: 'ProductInfo',
  components: {
    SearchArea,
    BreadCrumb,
    QuantityChanger,
    ProductDetailTabs,
    PromotionSection,
    RelatedProductsSection,
    CovidBanner
  },
  data() {
    return {
      quantity: 1,
      value: 0,
      product: {},
      loaded: false,
      moq: 0,
      incoterm: '',
      productTotal: ''
    }
  },
  watch: {
    '$route'() {
      this.getProductData()
    }
  },
  async created() {
    this.loaded = false
    await this.getProductData()
    document.title = this.product.name
  },
  computed: {
    illegalQuantity() {
      return this.quantity < this.moq
    }
  },
  methods: {
    ...mapActions([
      'getProductPreviewInfo',
      'addItemToCart'
    ]),
    async getProductData() {
      this.product = await this.getProductPreviewInfo(this.$route.params.id)
      if (!this.product.result) {
        this.$notify({
          group: 'buyHive',
          type: 'error',
          text: this.product.message
        })
        this.$router.go(-1)
        return
      }
      if (this.product.incoTerms.includes('FOB')) {
        const index = this.product.incoTerms.indexOf('FOB')
        this.product.incoTerms.splice(index, 1)
        this.product.incoTerms.unshift('FOB')
      }
      this.loaded = true
      this.moq = this.product.minimumOrderQuantity
      this.quantity = this.moq
      this.incoterm = this.product.incoTerms[0]
      this.updatePrice()
    },
    changeQuantity(val) {
      this.quantity = val
      this.updatePrice()
    },
    updatePrice() {
      const unit = this.product.unit
      this.product.pricing.some((priceRange) => {
        const quantityMax = String(priceRange.maxQuantity).replace(/,/g, "")
        if(this.quantity <= parseInt(quantityMax)){
          if(this.incoterm == 'EXW')
            this.productTotal = `${priceRange.displayExwPrice} / ${unit}`
          else if(this.incoterm == 'FOB')
            this.productTotal = `${priceRange.displayFobPrice} / ${unit}`
          return true
        }
        else{
          if(this.incoterm == 'EXW')
            this.productTotal = `$ ${this.product.lowestExwPrice} / ${unit}`
          else if(this.incoterm == 'FOB')
            this.productTotal = `$ ${this.product.lowestFobPrice} / ${unit}`
        }
      })
    },
    addToCart() { return }
  }
}
</script>
<style lang="scss" scoped>
::-webkit-scrollbar {
  width: 1px;
}

::-webkit-scrollbar-track {
  @apply bg-smoke;
}
 
::-webkit-scrollbar-thumb {
  @apply bg-gray-800
}
/deep/.list-styles-revert {
  ol,  ul {
    list-style: revert;
    margin: revert;
    padding: revert;
  }
}
.w-pro-info {
  @screen lg {
    width: calc(100% - 200px);
  }
}
</style>
